import React, { FC } from 'react';
import styled, { css } from 'styled-components';

interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
    icon?: JSX.Element;
    size?: 'small' | 'medium' | 'large';
    label: string;
    iconBefore?: boolean;
    spaceTop?: boolean;
    color?: 'outline' | 'muted' | 'grey' | 'link';
    alignSelf?: 'flex-start' | 'center' | 'flex-end';
}

interface ButtonStyleProps {
    readonly size?: 'small' | 'medium' | 'large';
    readonly color?: 'outline' | 'muted' | 'grey' | 'link';
    readonly alignSelf?: 'flex-start' | 'center' | 'flex-end';
    readonly ref?: any;
    readonly spaceTop?: boolean;
}

const StyledButton = styled.button<ButtonStyleProps>`
    ${({ theme, color, size, alignSelf, spaceTop }) => css`
        display: inline-flex;
        flex-direction: row;
        color: #fff;
        border: solid 2px ${theme.colors.alt};
        font-weight: 700;
        font-size: 16px;
        line-height: 44px;
        min-height: 44px;
        width: 100%;
        background-color: ${theme.colors.alt};
        transition: all 0.3s ease;
        border-radius: 3px;
        width: auto;
        padding-left: 10px;
        padding-right: 10px;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        outline: none;
        
}
        .icon {
            display: flex;
            justify-content: center;
        }

        .icon svg {
            width: 16px;
            height: auto;
        }

        .icon--before {
            margin-right: 10px;
        }
        .icon--after {
            margin-left: 10px;
        }

        ${
            size === 'small' &&
            css`
                font-size: 14px;

                line-height: 28px;
                min-height: 28px;

                .icon svg {
                    width: 12px;
                    height: auto;
                }
            `
        }
        ${
            size === 'large' &&
            css`
                font-size: 16px;

                line-height: 48px;
                min-height: 48px;
                padding-left: 24px;
                padding-right: 24px;

                .icon svg {
                    width: 16px;
                    height: auto;
                }
            `
        }
        ${
            color === 'outline' &&
            css`
                color: ${theme.colors.alt};
                background-color: ${theme.colors.white};
            `
        }
        ${
            alignSelf &&
            css`
                align-self: ${alignSelf};
            `
        }
        ${
            spaceTop &&
            css`
                margin-top: 24px;
            `
        }
        ${
            color === 'muted' &&
            css`
                color: ${theme.colors.white};
                background-color: ${theme.colors.dark};
                border-color: ${theme.colors.dark};
            `
        }
        ${
            color === 'grey' &&
            css`
                color: ${theme.colors.white};
                background-color: #858585;
                border-color: #858585;
            `
        }
        ${
            color === 'link' &&
            css`
                color: currentColor;
                background-color: transparent;
                border: none;
                padding-top: 4px;
                padding-bottom: 4px;
                line-height: 18px;
                min-height: 18px;
                text-decoration: underline;
                font-weight: normal;
                padding-left: 0;
                padding-right: 0;

                &:hover {
                    text-decoration: none;
                }
            `
        }
    `}
`;

const Button: FC<ButtonProps> = React.forwardRef(
    ({ label, icon, iconBefore, color, onClick, size, alignSelf, spaceTop, ...rest }, ref) => (
        <StyledButton
            type="button"
            className="sl-button"
            color={color}
            onClick={onClick}
            size={size}
            alignSelf={alignSelf}
            spaceTop={spaceTop}
            ref={ref}
            {...rest}
        >
            {icon && iconBefore && <span className="icon icon--before">{icon}</span>}
            <span className="sl-button-label">{label}</span>
            {icon && !iconBefore && <span className="icon icon--after">{icon}</span>}
        </StyledButton>
    )
);

export default Button;
